// react
import { useState } from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import {
  CareCircleInviteFields,
  isInviteDraftComplete,
  type RelationshipOption,
} from '@/pwa/molecules/CareCircleInviteFields/CareCircleInviteFields';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

export interface CareCircleInvitePageProps {
  relationshipOptions: RelationshipOption[];
  onSubmit: (data: {
    firstName: string;
    email: string;
    careRecipientAccountRelationshipId: string;
  }) => void | Promise<void>;
  onBack?: () => void;
  isSubmitting?: boolean;
}

/**
 * CareCircleInvitePage is a form page for inviting a new member to the care circle.
 */
export const CareCircleInvitePage = ({
  relationshipOptions,
  onSubmit,
  onBack,
  isSubmitting = false,
}: CareCircleInvitePageProps) => {
  const [draft, setDraft] = useState({
    firstName: '',
    email: '',
    careRecipientAccountRelationshipId: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isValid = isInviteDraftComplete(draft);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    if (!isValid) return;
    await onSubmit({
      firstName: draft.firstName.trim(),
      email: draft.email.trim(),
      careRecipientAccountRelationshipId:
        draft.careRecipientAccountRelationshipId,
    });
  };

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Invite to Care Circle'
            description='Once invited your care circle member can join conversations with your care expert, view key information, and access the library resources.'
          />
        }
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <CareCircleInviteFields
                value={draft}
                onChange={(value) => setDraft({ ...draft, ...value })}
                relationshipOptions={relationshipOptions}
                isDisabled={isSubmitting}
                isEveryErrorShown={isSubmitted}
              />
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  justifyContent: 'flex-end',
                  pt: 1,
                }}
              >
                <Button onClick={onBack} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={!isValid || isSubmitting}
                >
                  {isSubmitting ? 'Sending…' : 'Send email invite'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

CareCircleInvitePage.displayName = 'CareCircleInvitePage';

export default CareCircleInvitePage;
